import io from "socket.io-client";
import type { Donation } from "./types";

export class DonationAlertsSocket {
  private _processedIds: string[] = [];
  private _socket: SocketIOClient.Socket;

  constructor(token: string) {
    this._socket = io("wss://socket.donationalerts.ru:443", {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    });

    this._socket.emit("add-user", {
      token,
      type: "minor",
    });
  }

  /**
   * Listen for new donations
   */
  public onDonation(callback: (donation: Donation) => void): void {
    this._socket.on("donation", (message: string) => {
      if (!message || typeof message !== "string") {
        return;
      }

      try {
        const donation = JSON.parse(message);
        const id = String(donation.id);
        const alertType = String(donation.alert_type);

        // Basic validation of the donation message
        if (
          alertType !== "1" ||
          !donation.currency ||
          !donation.billing_system ||
          !donation.amount ||
          !donation.amount_main ||
          !donation.id
        ) {
          return;
        }

        // Prevent duplicate processing
        if (this._processedIds.includes(id)) {
          return;
        }

        const donationData: Donation = {
          id,
          alertType,
          amount: Number(donation.amount),
          currency: donation.currency,
          amountMain: Number(donation.amount_main),
          billingSystem: donation.billing_system,
        };

        callback(donationData);

        this._processedIds.push(id);
        // Keep the list size manageable
        if (this._processedIds.length > 100) {
          this._processedIds.shift();
        }
      } catch (error) {
        console.warn(`Failed to parse DonationAlerts message: ${message}`, error);
      }
    });
  }

  public onConnect(callback: () => void): void {
    this._socket.on("connect", () => {
      console.log("Connected to Donation Alerts");
      callback();
    });
  }

  public onError(callback: (error: Error) => void): void {
    this._socket.on("connect_error", (err: { message: string }) => {
      console.error(`Donation Alerts Connection Error: ${err.message}`);
      callback(new Error(err.message));
    });
  }

  public onDisconnect(callback: (reason: string) => void): void {
    this._socket.on("disconnect", (reason: string) => {
      console.log(`Donation Alerts Disconnected: ${reason}`);
      callback(reason);
    });
  }

  /**
   * Close the connection
   */
  public disconnect(): void {
    this._socket.disconnect();
  }
}
